import { create } from "zustand";
import { devtools as devtoolsMiddleware } from "zustand/middleware";
import type { Notification } from "../types/notification";

const devtools = import.meta.env.DEV
  ? devtoolsMiddleware
  : (((fn: unknown) => fn) as typeof devtoolsMiddleware);

const MAX_RECENT = 20;

interface NotificationState {
  unreadCount: number;
  recentNotifications: Notification[];

  // Actions
  setUnreadCount: (count: number) => void;
  addNotification: (notification: Notification) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  setRecentNotifications: (notifications: Notification[]) => void;
}

export const useNotificationStore = create<NotificationState>()(
  devtools(
    (set) => ({
      unreadCount: 0,
      recentNotifications: [],

      setUnreadCount: (count) =>
        set({ unreadCount: count }, undefined, "notifications/setUnreadCount"),

      addNotification: (notification) =>
        set(
          (state) => {
            // Deduplicate by id
            const filtered = state.recentNotifications.filter(
              (n) => n.id !== notification.id
            );
            // Prepend new notification, cap at MAX_RECENT
            const recentNotifications = [notification, ...filtered].slice(
              0,
              MAX_RECENT
            );
            const unreadCount = notification.is_read
              ? state.unreadCount
              : state.unreadCount + 1;
            return { recentNotifications, unreadCount };
          },
          undefined,
          "notifications/addNotification"
        ),

      markAsRead: (id) =>
        set(
          (state) => {
            const recentNotifications = state.recentNotifications.map((n) =>
              n.id === id
                ? { ...n, is_read: true, read_at: new Date().toISOString() }
                : n
            );
            const wasUnread = state.recentNotifications.find(
              (n) => n.id === id && !n.is_read
            );
            const unreadCount = wasUnread
              ? Math.max(0, state.unreadCount - 1)
              : state.unreadCount;
            return { recentNotifications, unreadCount };
          },
          undefined,
          "notifications/markAsRead"
        ),

      markAllAsRead: () =>
        set(
          (state) => ({
            recentNotifications: state.recentNotifications.map((n) =>
              n.is_read
                ? n
                : { ...n, is_read: true, read_at: new Date().toISOString() }
            ),
            unreadCount: 0,
          }),
          undefined,
          "notifications/markAllAsRead"
        ),

      setRecentNotifications: (notifications) =>
        set(
          { recentNotifications: (notifications ?? []).slice(0, MAX_RECENT) },
          undefined,
          "notifications/setRecentNotifications"
        ),
    }),
    { name: "NotificationStore" }
  )
);

// Expose getState for non-React contexts (SSE hook)
export { useNotificationStore as notificationStore };
